/**
 * SmartListsBar — global filter + saved-list chip strip.
 *
 * Renders on the Leads / Pipeline page. Reps can:
 *   • Click a saved smart list chip → shows results below (or emits onApply)
 *   • Click "+ Filter" → opens the rule builder dialog
 *   • Preview the count live, then Save as Smart List
 *
 * The rule tree matches smart_lists_service.py — pure Mongo AND/OR trees.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Filter, Trash2, Save, X, Loader2, Sparkles } from "lucide-react";

const OP_LABELS = {
  "=": "equals", "!=": "not equals", ">": "greater than", ">=": "≥",
  "<": "less than", "<=": "≤",
  "contains": "contains", "not_contains": "does not contain",
  "in": "any of", "not_in": "none of",
  "before": "before", "after": "after", "days_since_gte": "days since ≥",
  "exists": "is set",
};

const FIELD_LABELS = {
  property_equity_pct: "Equity %",  property_arv: "ARV",
  asking_price: "Asking Price",     mortgage_balance: "Mortgage Balance",
  ownership_years: "Years Owned",   motivation_score: "Motivation Score",
  sms_ai_score: "AI SMS Score",     city: "City",
  zip: "ZIP",                        county: "County",  state: "State",
  property_type: "Property Type",    column_key: "Pipeline Stage",
  list_name: "List",                 source: "Source",
  assigned_to: "Assigned To",        owner_occupied: "Owner-Occupied",
  last_contact_at: "Last Contact",   next_followup_at: "Next Follow-Up",
  tags: "Tag",                        board_id: "Board",
};

function emptyRule(field = "property_equity_pct") {
  return { field, op: ">=", value: "" };
}

export default function SmartListsBar({ onApply }) {
  const [catalog, setCatalog]   = useState(null);
  const [lists, setLists]       = useState([]);
  const [tagLib, setTagLib]     = useState([]);
  const [showBuilder, setBuild] = useState(false);
  const [tree, setTree]         = useState({ op: "AND", rules: [emptyRule()] });
  const [preview, setPreview]   = useState({ count: null, sample: [] });
  const [previewBusy, setPB]    = useState(false);
  const [saveName, setSN]       = useState("");
  const [active, setActive]     = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [f, l, t] = await Promise.all([
          api.get("/smart-lists/fields"),
          api.get("/smart-lists"),
          api.get("/tags"),
        ]);
        setCatalog(f.data);
        setLists(l.data.smart_lists || []);
        setTagLib(t.data.tags || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const runPreview = async (t = tree) => {
    setPB(true);
    try {
      const { data } = await api.post("/smart-lists/preview", { tree: t, limit: 25 });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPB(false); }
  };

  const applySaved = async (sl) => {
    setActive(sl.id);
    setTree(sl.tree || { op: "AND", rules: [] });
    setPB(true);
    try {
      const { data } = await api.get(`/smart-lists/${sl.id}/leads?limit=100`);
      setPreview({ count: data.count, sample: data.sample });
      onApply?.({ id: sl.id, name: sl.name, count: data.count, leads: data.sample });
    } catch { toast.error("Load failed"); }
    setPB(false);
  };

  const save = async () => {
    if (!saveName.trim()) return toast.error("Name your smart list first");
    try {
      const { data } = await api.post("/smart-lists", { name: saveName, tree });
      setLists((prev) => [data.smart_list, ...prev]);
      setSN("");
      toast.success(`Saved "${data.smart_list.name}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this smart list?")) return;
    try {
      await api.delete(`/smart-lists/${id}`);
      setLists((prev) => prev.filter((l) => l.id !== id));
      if (active === id) setActive(null);
    } catch { toast.error("Delete failed"); }
  };

  const setRule  = (i, patch) => setTree((t) => ({ ...t, rules: t.rules.map((r, j) => j === i ? { ...r, ...patch } : r) }));
  const addRule  = () => setTree((t) => ({ ...t, rules: [...t.rules, emptyRule()] }));
  const dropRule = (i) => setTree((t) => ({ ...t, rules: t.rules.filter((_, j) => j !== i) }));

  const fieldType = (field) => (catalog?.fields || []).find((f) => f.key === field)?.type || "string";
  const opsFor    = (field) => catalog?.ops_by_type?.[fieldType(field)] || ["="];

  return (
    <div className="space-y-2" data-testid="smart-lists-bar">
      <div className="flex flex-wrap items-center gap-1.5">
        {lists.map((l) => (
          <div key={l.id}
               className={`group inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold cursor-pointer transition ${
                 active === l.id ? "bg-slate-900 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
               }`}
               onClick={() => applySaved(l)}
               data-testid={`sl-chip-${l.id}`}>
            <Sparkles size={10} />
            {l.name}
            <button onClick={(e) => { e.stopPropagation(); del(l.id); }}
                    className="opacity-0 group-hover:opacity-100 ml-0.5 rounded-full hover:bg-black/20">
              <Trash2 size={10} />
            </button>
          </div>
        ))}
        <button onClick={() => setBuild((v) => !v)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-400 px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                data-testid="sl-open-builder">
          <Filter size={11} /> {showBuilder ? "Close Filter" : "+ Filter"}
        </button>
      </div>

      {showBuilder && catalog && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Match</span>
            <select value={tree.op}
                    onChange={(e) => setTree((t) => ({ ...t, op: e.target.value }))}
                    className="text-xs border border-slate-200 rounded px-1.5 py-0.5">
              <option value="AND">ALL of these</option>
              <option value="OR">ANY of these</option>
            </select>
          </div>

          {tree.rules.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 mb-1.5" data-testid={`sl-rule-${i}`}>
              <select value={r.field}
                      onChange={(e) => setRule(i, { field: e.target.value, op: opsFor(e.target.value)[0], value: "" })}
                      className="text-xs border border-slate-200 rounded px-1.5 py-1 min-w-[130px]">
                {Object.entries(FIELD_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
              <select value={r.op}
                      onChange={(e) => setRule(i, { op: e.target.value })}
                      className="text-xs border border-slate-200 rounded px-1.5 py-1">
                {opsFor(r.field).map((op) => (
                  <option key={op} value={op}>{OP_LABELS[op] || op}</option>
                ))}
              </select>
              {r.field === "tags" ? (
                <select value={r.value}
                        onChange={(e) => setRule(i, { value: e.target.value })}
                        className="text-xs border border-slate-200 rounded px-1.5 py-1 min-w-[140px]">
                  <option value="">— pick tag —</option>
                  {tagLib.map((t) => <option key={t.id} value={t.slug}>{t.name}</option>)}
                </select>
              ) : fieldType(r.field) === "boolean" ? (
                <select value={String(r.value)}
                        onChange={(e) => setRule(i, { value: e.target.value === "true" })}
                        className="text-xs border border-slate-200 rounded px-1.5 py-1">
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input value={r.value ?? ""}
                       onChange={(e) => setRule(i, {
                         value: fieldType(r.field) === "number" ? Number(e.target.value) : e.target.value,
                       })}
                       placeholder={fieldType(r.field) === "date" ? "days" : "value"}
                       className="text-xs border border-slate-200 rounded px-1.5 py-1 flex-1 min-w-[100px]" />
              )}
              <button onClick={() => dropRule(i)} className="text-slate-400 hover:text-red-500">
                <X size={12} />
              </button>
            </div>
          ))}

          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
            <button onClick={addRule}
                    className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <Plus size={11} /> Add rule
            </button>
            <div className="flex-1" />
            <button onClick={() => runPreview()}
                    disabled={previewBusy}
                    className="text-[11px] bg-slate-900 text-white rounded-md px-3 py-1 hover:bg-slate-700 disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="sl-preview-btn">
              {previewBusy ? <Loader2 size={11} className="animate-spin" /> : <Filter size={11} />}
              Preview
            </button>
          </div>

          {preview.count !== null && (
            <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-600">
              <b>{preview.count}</b> lead{preview.count === 1 ? "" : "s"} match
            </div>
          )}

          <div className="mt-2 flex items-center gap-1.5">
            <input value={saveName} onChange={(e) => setSN(e.target.value)}
                   placeholder="Name this smart list (e.g., 🔥 Hot SWFL)"
                   className="flex-1 text-xs border border-slate-200 rounded px-2 py-1"
                   data-testid="sl-save-name" />
            <button onClick={save}
                    className="text-[11px] bg-emerald-600 text-white rounded-md px-3 py-1 hover:bg-emerald-700 inline-flex items-center gap-1"
                    data-testid="sl-save-btn">
              <Save size={11} /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
